import { FC, useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { SketchPicker } from 'react-color';
import { useTheme } from '@shared/components/ui/theme-provider';
import './ColorPicker.css';

interface ColorPickerProps {
  value?: string;
  onChange: (color: string) => void;
  disabled?: boolean;
  /** 'sm' = pelotita pequeña para ir dentro del input; 'md' = tamaño normal */
  size?: 'sm' | 'md';
}

const DEFAULT_COLOR = '#000000';

/** Normaliza hex a #RRGGBB (6 dígitos) para compatibilidad */
function normalizeHex(hex: string): string {
  const clean = (hex || '').replace(/^#/, '').trim();
  if (clean.length === 3 && /^[0-9A-Fa-f]+$/.test(clean)) {
    const r = clean[0] + clean[0];
    const g = clean[1] + clean[1];
    const b = clean[2] + clean[2];
    return `#${r}${g}${b}`.toLowerCase();
  }
  if (clean.length === 6 && /^[0-9A-Fa-f]+$/.test(clean)) {
    return `#${clean}`.toLowerCase();
  }
  return DEFAULT_COLOR;
}

/** Detecta si el tema resuelto es oscuro (clase en document) para SketchPicker */
function useResolvedDark(): boolean {
  const { theme } = useTheme();
  const [isDark, setIsDark] = useState(() =>
    typeof document !== 'undefined' ? document.documentElement.classList.contains('dark') : false,
  );

  useEffect(() => {
    const root = document.documentElement;
    const update = () => setIsDark(root.classList.contains('dark'));
    update();
    const observer = new MutationObserver(update);
    observer.observe(root, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, [theme]);

  return isDark;
}

export const ColorPicker: FC<ColorPickerProps> = ({
  value = DEFAULT_COLOR,
  onChange,
  disabled = false,
  size = 'md',
}) => {
  const isDark = useResolvedDark();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedColor, setSelectedColor] = useState(normalizeHex(value));
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setSelectedColor(normalizeHex(value));
  }, [value]);

  const PICKER_WIDTH = 200;
  const getPopoverPosition = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return { top: 0, left: 0 };
    const rect = el.getBoundingClientRect();
    const padding = 8;
    const pickerHeight = 280;
    const pickerWidth = PICKER_WIDTH;
    const viewportW = window.innerWidth;
    const viewportH = window.innerHeight;
    let top = rect.bottom + padding;
    let left = rect.left;
    if (top + pickerHeight > viewportH - padding) {
      top = Math.max(padding, rect.top - pickerHeight - padding);
    }
    if (left + pickerWidth > viewportW - padding) {
      left = viewportW - pickerWidth - padding;
    }
    if (left < padding) left = padding;
    return { top, left };
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    setPosition(getPopoverPosition());
    const handleScrollOrResize = () => setPosition(getPopoverPosition());
    window.addEventListener('scroll', handleScrollOrResize, true);
    window.addEventListener('resize', handleScrollOrResize);
    return () => {
      window.removeEventListener('scroll', handleScrollOrResize, true);
      window.removeEventListener('resize', handleScrollOrResize);
    };
  }, [isOpen, getPopoverPosition]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (triggerRef.current?.contains(target) || popoverRef.current?.contains(target)) {
        return;
      }
      setIsOpen(false);
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isOpen) return;
      if (event.key === 'Enter') {
        event.preventDefault();
        setIsOpen(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  // Detectar clics en colores predefinidos para cerrar el picker
  useEffect(() => {
    if (!isOpen) return;

    const handlePresetColorClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      // Detectar si el clic fue en un color predefinido (div dentro de la última sección del picker)
      const presetSection = document.querySelector('.sketch-picker > div:last-child');
      if (presetSection && presetSection.contains(target)) {
        // Verificar que el clic fue en un div de color (no en el contenedor)
        const colorDiv = target.closest('div[style*="background"]');
        if (colorDiv && colorDiv !== presetSection) {
          setTimeout(() => setIsOpen(false), 100);
        }
      }
    };

    // Usar capture phase para capturar el evento antes de que llegue al SketchPicker
    document.addEventListener('click', handlePresetColorClick, true);
    return () => {
      document.removeEventListener('click', handlePresetColorClick, true);
    };
  }, [isOpen]);

  const handleChange = (color: { hex: string }) => {
    const hex = color.hex ?? selectedColor;
    setSelectedColor(hex);
    onChange(hex);
  };

  const handleChangeComplete = (color: { hex: string }) => {
    const hex = color.hex ?? selectedColor;
    setSelectedColor(hex);
    onChange(hex);
    // NO cerrar aquí - solo actualizar el color
    // El cierre se manejará solo cuando se hace clic en colores predefinidos
  };

  const displayColor = normalizeHex(selectedColor);

  const sketchPickerStyles = isDark
    ? {
        picker: {
          background: 'var(--wrh-card-bg)',
          boxShadow: '0 0 0 1px var(--wrh-card-border), 0 8px 16px rgba(0,0,0,.4)',
        },
      }
    : undefined;

  // z-index máximo para quedar siempre por encima del modal (Radix Dialog usa z-50)
  const COLOR_PICKER_Z_INDEX = 2147483647;

  const popoverContent = isOpen ? (
    <div
      data-testid="color-picker-overlay"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: COLOR_PICKER_Z_INDEX,
        pointerEvents: 'auto',
      }}
    >
      {/* Overlay que captura clics fuera del picker; fondo casi transparente para que reciba eventos */}
      <div
        role="presentation"
        aria-hidden
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          pointerEvents: 'auto',
          backgroundColor: 'transparent',
        }}
        onClick={() => setIsOpen(false)}
        onMouseDown={(e) => e.stopPropagation()}
      />
      <div
        ref={popoverRef}
        className={isDark ? 'color-picker-popover-dark' : ''}
        style={{
          position: 'absolute',
          top: position.top,
          left: position.left,
          zIndex: 1,
          width: PICKER_WIDTH,
          pointerEvents: 'auto',
          overflow: 'hidden',
          borderRadius: '4px',
        }}
        data-testid="color-picker-popover"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <SketchPicker
          width={PICKER_WIDTH}
          color={displayColor}
          onChange={handleChange}
          onChangeComplete={handleChangeComplete}
          disableAlpha
          styles={sketchPickerStyles}
        />
      </div>
    </div>
  ) : null;

  const isSmall = size === 'sm';
  return (
    <div ref={triggerRef} className="relative inline-block">
      <button
        type="button"
        onClick={() => {
          if (disabled) return;
          if (!isOpen) {
            setPosition(getPopoverPosition());
          }
          setIsOpen((prev) => !prev);
        }}
        disabled={disabled}
        className={`rounded-full border-2 border-card-border disabled:opacity-50 disabled:cursor-not-allowed ${isSmall ? 'w-5 h-5' : 'w-8 h-8'}`}
        style={{ backgroundColor: displayColor }}
        data-testid="color-picker-button"
        aria-label="Seleccionar color"
      />
      {popoverContent && createPortal(popoverContent, document.body)}
    </div>
  );
};
